import { Flex, useStyles } from '@archipro-design/aria';
import { CollaboratorAvatar } from '@modules/design-board/component/manage/collaborator-overview/components/CollaboratorAvatar';
import { ResendInviteLink } from '@modules/design-board/component/manage/collaborator-overview/components/ResendInviteLink';
import { CollaboratorPermissionToggle } from '@modules/design-board/component/share-modal/CollaboratorPermissionToggle';
import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import type { CollaboratorAccessType } from '@modules/design-board/validation/schemas';
import { useFetcher, useLocation } from '@remix-run/react';
import { useDesignBoardPageState } from '@modules/design-board/component/context';
import * as S from '@modules/design-board/component/manage-collaborators/MobileCollaboratorsModal.style';
import { AllowedActions } from '@modules/design-board/actions/handler-mappings';
import { useLogger } from '@archipro-website/logger/client';
import { useDesignBoardCollaboratorsContext } from '@modules/design-board/component/manage/collaborator-overview/context/DesignBoardCollaboratorsContext';
import { Confirmation } from '@modules/design-board/component/manage/collaborator-overview/components/Confirmation';
import { useMemoizedFn } from 'ahooks';

interface MobileCollaboratorListItemProps {
    name: string;
    imageUrl: string;
    membership: 'owners' | 'collaborators';
    inviteStatus: 'accepted' | 'sent';
    email?: string;
    access: 'edit' | 'view' | 'comment';
}

export const MobileCollaboratorListItem = ({
    name,
    imageUrl,
    membership,
    inviteStatus,
    email,
    access,
}: MobileCollaboratorListItemProps) => {
    const { css } = useStyles();
    const logger = useLogger();
    const location = useLocation();
    const fetcher = useFetcher();
    const {
        state: { currentBoardData },
    } = useDesignBoardPageState();
    const [userAccess, setUserAccess] = useState(access);
    const context = useDesignBoardCollaboratorsContext();

    const updatePermission = (permission: CollaboratorAccessType) => {
        if (currentBoardData && email) {
            fetcher.submit(
                {
                    designBoardId: String(currentBoardData.ID),
                    email: email,
                    canEdit: String(permission === 'edit'),
                    hasAccepted: String(inviteStatus === 'accepted'),
                    _action: AllowedActions.enum.update_collaborator_permission,
                },
                {
                    method: 'post',
                    action: location.pathname,
                }
            );
        } else {
            logger.error('attempted to permission change without user data');
        }
    };

    const removeCollaborator = () => {
        if (!context.state?.removingCollaborator) return;
        const removed = context.state.removingCollaborator;
        fetcher.submit(
            {
                email: removed.email,
                boardId: `${context.state.boardId}`,
                inviteStatus: removed.inviteStatus,
                _action: AllowedActions.enum.remove_collaborator,
            },
            {
                method: 'post',
                action: location.pathname,
            }
        );
    };

    const optimisticRemove = useMemoizedFn(() => {
        if (!context.state?.removingCollaborator) return;
        const removed = context.state.removingCollaborator;
        context.dispatch({
            type: 'confirm-collaborator-removal',
            data: { email: removed.email, inviteStatus: removed.inviteStatus },
        });
        context.dispatch({ type: 'hide-remove-collaborator-confirmation' });
    });

    useEffect(() => {
        if (fetcher.state === 'loading') {
            const action = fetcher.formData?.get('_action');
            const email = fetcher.formData?.get('email');
            const inviteStatus = fetcher.formData?.get('inviteStatus');
            if (
                action === AllowedActions.enum.remove_collaborator &&
                email &&
                inviteStatus
            ) {
                optimisticRemove();
            }
        }
    }, [fetcher, optimisticRemove]);

    const updateCollaborator = (permission: CollaboratorAccessType | null) => {
        if (permission) {
            updatePermission(permission);
        } else if (email) {
            context.dispatch({
                type: 'show-remove-collaborator-confirmation',
                data: {
                    email: email,
                    inviteStatus,
                },
            });
        }
    };

    const renderMode =
        membership === 'owners'
            ? 'owner'
            : membership === 'collaborators' && inviteStatus === 'accepted'
            ? 'collaborator'
            : 'invited';

    const collaboratorItem = (): ReactNode => {
        switch (renderMode) {
            case 'owner': {
                return <p>Owner</p>;
            }
            case 'invited': {
                return email ? <ResendInviteLink email={email} /> : null;
            }
            case 'collaborator': {
                return email ? (
                    <CollaboratorPermissionToggle
                        bottomMenuData={{
                            type: 'permission',
                            canRemove: true,
                            canEdit: userAccess === 'edit',
                            canView: userAccess === 'view',
                            canComment: userAccess === 'comment',
                            name: name,
                            email: email,
                            profileImageUrl: imageUrl,
                            changePermission: (permission) => {
                                if (permission) {
                                    setUserAccess(permission);
                                }
                                updateCollaborator(permission);
                            },
                        }}
                        permission={userAccess}
                        shortDescription={true}
                    />
                ) : null;
            }
            default:
                return null;
        }
    };

    return (
        <div className={css(S.CollaboratorsListItem)}>
            <CollaboratorAvatar name={name} imageUrl={imageUrl} />
            <Flex vAlign={'center'} hAlign={'end'} design={{ width: '100%' }}>
                {collaboratorItem()}
            </Flex>
            <Confirmation
                title={'Remove collaborator'}
                content={'Are you sure you would like to remove collaborator?'}
                confirmText={'REMOVE'}
                cancelText={'CANCEL'}
                onConfirm={removeCollaborator}
            />
        </div>
    );
};
